import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    use,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import noop from 'lodash/noop';
import { concatMap, Observable, Subject } from 'rxjs';
import { ChipData } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { FilterUIAction, PreAppliedFilters } from '@interfaces/central-monitoring';
import { usePerformFilterUIAction } from '@apis/bff-api/central-monitoring';

export type StudyDataFilterState = {
    sessionId: string;
    setSessionId: Dispatch<SetStateAction<string>>;
    filtersChanged$: Observable<void>;
    isFiltered: boolean;
    setIsFiltered: Dispatch<SetStateAction<boolean>>;
    isRecalculating: boolean;
    chipData: Array<ChipData>;
    setChipData: Dispatch<SetStateAction<Array<ChipData>>>;
    preAppliedFilters: PreAppliedFilters;
    copySessionId?: string;
};

export const StudyDataFilterContext = createContext<StudyDataFilterState>({
    sessionId: '',
    setSessionId: noop,
    filtersChanged$: new Observable(),
    isFiltered: false,
    setIsFiltered: noop,
    isRecalculating: false,
    chipData: [],
    setChipData: noop,
    preAppliedFilters: { filters: [] },
});

const filtersChangedSubject$ = new Subject<void>();
const httpRequestQueue$ = new Subject<() => Promise<unknown>>();

type Props = {
    children: ReactNode;
    studyRef: string;
    datasetId: number;
    dataViewRef?: string;
    recalculateOnDatasetChange?: boolean;
    preAppliedFilters: PreAppliedFilters;
    copySessionId?: string;
};

export const StudyDataFilterContextProvider = ({
    children,
    studyRef,
    datasetId,
    dataViewRef,
    recalculateOnDatasetChange,
    preAppliedFilters,
    copySessionId,
}: Props) => {
    const [sessionId, setSessionId] = useState<string>('');
    const [isFiltered, setIsFiltered] = useState<boolean>(false);
    const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
    const [chipData, setChipData] = useState<ChipData[]>([]);
    const firstRenderRef = useRef<boolean>(true);
    const { trigger: performFilterValueAction } = usePerformFilterUIAction(studyRef, sessionId, datasetId, dataViewRef);

    useEffect(() => {
        const subscription = httpRequestQueue$.pipe(concatMap((r) => r())).subscribe();

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSessionId('');
    }, [dataViewRef]);

    // Do not make the request the first time the context is rendered. It is unnecessary
    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        if (!recalculateOnDatasetChange) return;

        // Set a value on context so we know a dataset recalc is happening
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsRecalculating(true);
        httpRequestQueue$.next(() =>
            performFilterValueAction({ action: FilterUIAction.DatasetChange })
                .then(() => {
                    setIsRecalculating(false);
                    if (isFiltered) filtersChangedSubject$.next();
                })
                .catch((err) => {
                    window.console.error(err);
                    setIsRecalculating(false);
                }),
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [datasetId]);

    return (
        <StudyDataFilterContext
            value={useMemo(
                () => ({
                    sessionId,
                    setSessionId,
                    filtersChanged$: filtersChangedSubject$.asObservable(),
                    isFiltered,
                    setIsFiltered,
                    isRecalculating,
                    chipData,
                    setChipData,
                    preAppliedFilters,
                    copySessionId,
                }),
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/exhaustive-deps
                [
                    sessionId,
                    filtersChangedSubject$,
                    isFiltered,
                    isRecalculating,
                    chipData,
                    preAppliedFilters,
                    copySessionId,
                ],
            )}
        >
            {children}
        </StudyDataFilterContext>
    );
};

export const useStudyDataFilterContext = () => use(StudyDataFilterContext);

export const getEmitFilterChangedEvent = () => {
    const emitFilterChangedEvent = () => {
        filtersChangedSubject$.next();
    };

    return {
        emitFilterChangedEvent,
    };
};

export const getFilterHttpRequestQueue = () => httpRequestQueue$;
